/**
 * Derives auth-on-file RPA error UI state (message, dropdown placement) from order data.
 *
 * @module useAuthOnFileError
 */
import { useMemo } from "react";
import { useDataAccumulatorForSingleOrder } from "../context/dataAccumulatorForSingleOrder";
import { GeneralOrderDetails } from "../data-model/medicalPaOrdersModel";
import { AuthOnFileRpaStatus } from "../enums/authOnFileAuthStatus";
import { AuthOnFileRpaErrorType } from "../enums/authOnFileRpaErrorType";
import { DropdownLocation } from "../enums/dropdownLocation";
import { getErrorType } from "../pages/singleOrderLayout/utils/checkRpaError";
import { formatErrorType } from "../pages/singleOrderLayout/utils/errorFormatter";
import { getAllJCodesFromAuthOnFile } from "../pages/singleOrderLayout/utils/existingDocsToLs";

/**
 * @param authOnFile - Auth-on-file payload from the data accumulator
 * @returns `true` when BO report rows are absent or empty
 */
const isBusinessOfficeRowsMissing = (authOnFile: any): boolean => {
  return (
    !authOnFile ||
    !authOnFile.reports ||
    (Array.isArray(authOnFile.reports) && authOnFile.reports.length === 0)
  );
};

/**
 * @param authOnFile - Auth-on-file payload from the data accumulator
 * @param jCodes - Normalised J-code rows for the order
 * @returns `true` when no usable regimen / J-code rows exist
 */
const isRegimenMissing = (
  authOnFile: any,
  jCodes: any[] | null | undefined,
): boolean => {
  return !authOnFile || !jCodes || jCodes.filter(Boolean).length === 0;
};

/**
 * @param authOnFile - Auth-on-file payload from the data accumulator
 * @returns `true` when visit chart data is missing or empty
 */
const isVisitDateContentMissing = (authOnFile: any): boolean => {
  return (
    !authOnFile ||
    !authOnFile.visit_data_charts ||
    Object.keys(authOnFile.visit_data_charts).length === 0
  );
};

/**
 * @param expectedErrorType - Which RPA error variant to evaluate
 * @param authOnFile - Auth-on-file payload from the data accumulator
 * @param jCodes - Normalised J-code rows for the order
 * @returns `true` when the order data matches the expected missing-data pattern
 */
const checkIfErrorTypePresent = (
  expectedErrorType: AuthOnFileRpaErrorType,
  authOnFile: any,
  jCodes: any[] | null | undefined,
): boolean => {
  switch (expectedErrorType) {
    case AuthOnFileRpaErrorType.BUSINESS_OFFICE_ROWS_NOT_FOUND:
      return isBusinessOfficeRowsMissing(authOnFile);
    case AuthOnFileRpaErrorType.REGIMEN_NOT_FOUND:
      return isRegimenMissing(authOnFile, jCodes);
    case AuthOnFileRpaErrorType.VISIT_DATE_CONTENT_NOT_FOUND:
      return isVisitDateContentMissing(authOnFile);
    default:
      return false;
  }
};

/**
 * @param errorType - Raw error type string from {@link getErrorType}, if any
 * @returns Flags controlling error banner visibility and dropdown targeting
 */
const handleAuthOnFileError = (errorType: string | undefined) => {
  if (!errorType) {
    return {
      shouldShowError: false,
      errorMessage: "",
      dropdownLocation: DropdownLocation.NONE,
      isCompletedState: false,
    };
  }

  return {
    shouldShowError: true,
    errorMessage: formatErrorType(errorType),
    dropdownLocation: DropdownLocation.BOTH,
    isCompletedState: false,
  };
};

/**
 * @param isBusinessOfficePresent - Whether BO report rows exist on auth-on-file
 * @param isCptTablePresent - Whether at least one J-code row exists
 * @returns UI flags for post-completion validation problems
 */
const handleCompletedStateError = (
  isBusinessOfficePresent: boolean | undefined,
  isCptTablePresent: boolean | undefined,
) => {
  if (!isCptTablePresent) {
    return {
      shouldShowError: true,
      errorMessage: "No valid drug scheduled under this regimen",
      dropdownLocation: DropdownLocation.CPT,
      isCompletedState: true,
    };
  }

  if (!isBusinessOfficePresent) {
    return {
      shouldShowError: true,
      errorMessage: "Drug scheduled but business office not found",
      dropdownLocation: DropdownLocation.BUSINESS_OFFICE,
      isCompletedState: true,
    };
  }

  return {
    shouldShowError: false,
    errorMessage: "",
    dropdownLocation: DropdownLocation.NONE,
    isCompletedState: false,
  };
};

/**
 * Computes whether to show an auth-on-file error for a specific RPA failure mode.
 *
 * @param expectedErrorType - RPA error category this UI surface cares about
 * @returns Whether to show an error, human-readable copy, dropdown placement, and completed-state hint
 */
export const useAuthOnFileError = (
  expectedErrorType: AuthOnFileRpaErrorType,
) => {
  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();
  const authOnFile = authOnFileDetails?.auth_on_file;
  const errorType = getErrorType(
    generalOrderDetails?.order ?? ({} as GeneralOrderDetails),
  );
  const authOnFileStatus =
    generalOrderDetails?.order?.status?.auth_on_file_completion_status;
  const isAuthOnFileError = authOnFileStatus === AuthOnFileRpaStatus.ERROR;
  const isAuthOnFileCompleted =
    authOnFileStatus === AuthOnFileRpaStatus.COMPLETED;

  const jCodes = useMemo(
    () => getAllJCodesFromAuthOnFile(authOnFileDetails?.auth_on_file),
    [authOnFileDetails?.auth_on_file],
  );
  const isCptTablePresent =
    jCodes && Array.isArray(jCodes) && jCodes.filter(Boolean).length > 0;

  const isBusinessOfficePresent =
    authOnFile?.reports &&
    Array.isArray(authOnFile.reports) &&
    authOnFile.reports.length > 0;

  // Handle error state when auth on file has an error
  if (isAuthOnFileError) {
    return handleAuthOnFileError(errorType);
  }

  // Handle completed state with validation errors
  if (isAuthOnFileCompleted) {
    const isErrorTypePresent = checkIfErrorTypePresent(
      expectedErrorType,
      authOnFile,
      jCodes,
    );

    if (isErrorTypePresent) {
      return handleCompletedStateError(
        isBusinessOfficePresent,
        isCptTablePresent,
      );
    }
  }

  // No error to show
  return {
    shouldShowError: false,
    errorMessage: "",
    dropdownLocation: DropdownLocation.NONE,
    isCompletedState: false,
  };
};
